import { AppContext } from "@/App.context";
import config from "@/config";
import { DownloadFile } from "@/lib/download-file";
import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";

/**
 * @typedef Recommendation
 * @property {string} account
 * @property {string} resourceName
 * @property {string} resourceGroupName
 * @property {string} resourceId
 * @property {string} region
 * @property {string} currentType
 * @property {string} owner
 * @property {'upsize' | 'downsize' | 'terminate'} recommendationType
 * @property {string} recommendedType
 * @property {string} currentType
 * @property {number} savings
 * @property {number} riSavings
 * @property {number} utilization_cpu
 * @property {number} utilization_mem
 * @property {number} utilization_disk
 * @property {number} cost
 */

/**
 * @typedef GetRecommendationsListRes
 * @property {Recommendation[]} recommendations
 * @property {number} total
 */

/**
 * @typedef GetRecommendationsFilters
 * @property {string[]} accounts
 * @property {string[]} regions
 */

/**
 * @typedef GetRecommendationsListReq
 * @property {'aws' | 'gcp' | 'azure' | 'all'} service
 * @property {'downsize' | 'upsize' | 'terminate'} recommendationType
 */

export const getRecommendationsList = (token) => {
  /**
   * @param {GetRecommendationsListReq} req
   * @returns {Promise<GetRecommendationsListRes>}
   */
  return async (
    { recommendationType, service,},
    signal
  ) => {
    const recommendationsList = await fetch(
      `${config.apiHost}/v1/${service}/recommendations/download-all`,
      {
        method: "POST",
        body: JSON.stringify({
          recommendationType,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        signal,
      }
    );
    if (!recommendationsList.ok) {
      throw new Error((await recommendationsList.json()).message);
    }
    const reader = recommendationsList.body.getReader();
    const stream = new ReadableStream({
      start(controller) {
        function push() {
          reader.read().then(({ done, value }) => {
            if (done) {
              controller.close();
              return;
            }
            controller.enqueue(value);
            push();
          });
        }
        push();
      },
    });
    const blob = await new Response(stream).blob();
    const fileName = `Rightsizing-data.csv`;
    DownloadFile(blob, fileName);

  };
};

/**
 * @param {GetRecommendationsListReq} req
 * @param {any[]=} deps
 */
export const useRecommendationsListDownload = (req, deps = []) => {
  const appContext = useContext(AppContext);
  const query = useMutation({
    mutationKey: ["getRecommendationsList", req, ...deps],
    mutationFn: ({ signal }) =>
      getRecommendationsList(appContext.idToken)(req, signal),
  });
  return query;
};
